<script setup>
import { ref, computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  searchValue: {
    type: String,
    default: '',
  },
  sortBy: {
    type: String,
    default: 'created_at',
  },
  sortOrder: {
    type: String,
    default: '-',
  },
  showFilter: {
    type: Boolean,
    default: false,
  },
  isFilterOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:searchValue', 'update:sort', 'clickFilter']);

const { t } = useI18n();

// 排序弹窗
const isSortMenuOpen = ref(false);

// 排序选项
const sortMenus = computed(() => [
  {
    label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.SORT_NAME'),
    value: 'name',
  },
  {
    label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.SORT_CREATED_AT'),
    value: 'created_at',
  },
  {
    label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.SORT_UPDATED_AT'),
    value: 'updated_at',
  },
]);

// 排序顺序选项
const orderingMenus = computed(() => [
  {
    label: t('PILOT.KNOWLEDGE_BASES.SORT.ASCENDING'),
    value: '',
  },
  {
    label: t('PILOT.KNOWLEDGE_BASES.SORT.DESCENDING'),
    value: '-',
  },
]);

// 使用 toRef 确保响应式
const activeSort = toRef(props, 'sortBy');
const activeOrdering = toRef(props, 'sortOrder');

const activeSortLabel = computed(() => {
  const selectedMenu = sortMenus.value.find(
    menu => menu.value === activeSort.value
  );
  return (
    selectedMenu?.label || t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.SORT_CREATED_AT')
  );
});

const activeOrderingLabel = computed(() => {
  const selectedMenu = orderingMenus.value.find(
    menu => menu.value === activeOrdering.value
  );
  return selectedMenu?.label || t('PILOT.KNOWLEDGE_BASES.SORT.DESCENDING');
});

const handleSearchInput = event => {
  emit('update:searchValue', event.target.value);
};

const handleSortChange = value => {
  emit('update:sort', { sort: value, order: props.sortOrder });
};

const handleOrderChange = value => {
  emit('update:sort', { sort: props.sortBy, order: value });
};

const closeSortMenu = () => {
  isSortMenuOpen.value = false;
};
</script>

<template>
  <div class="flex flex-wrap items-center gap-3 px-0">
    <!-- 搜索框 -->
    <div class="flex-1 min-w-[200px] max-w-md">
      <Input
        :model-value="searchValue"
        type="search"
        :placeholder="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.SEARCH_PLACEHOLDER')"
        :custom-input-class="[
          'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
        ]"
        class="w-full"
        @input="handleSearchInput"
      >
        <template #prefix>
          <Icon
            icon="i-lucide-search"
            class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
          />
        </template>
      </Input>
    </div>

    <!-- 筛选和排序按钮 -->
    <div class="flex gap-2">
      <!-- 筛选按钮 -->
      <Button
        v-if="showFilter"
        id="toggleKnowledgeBaseDocumentsFilterButton"
        icon="i-lucide-sliders-horizontal"
        color="slate"
        size="sm"
        variant="ghost"
        :class="{ 'bg-n-alpha-2': isFilterOpen }"
        @click="$emit('clickFilter')"
      />

      <!-- 排序按钮 -->
      <div class="relative">
        <Button
          icon="i-lucide-arrow-down-up"
          color="slate"
          size="sm"
          variant="ghost"
          :class="isSortMenuOpen ? 'bg-n-alpha-2' : ''"
          @click="isSortMenuOpen = !isSortMenuOpen"
        />

        <!-- 排序弹窗 -->
        <div
          v-if="isSortMenuOpen"
          v-on-clickaway="closeSortMenu"
          class="absolute top-full mt-1 ltr:-right-32 rtl:-left-32 sm:ltr:right-0 sm:rtl:left-0 flex flex-col gap-4 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak w-72 rounded-xl p-4 z-20"
        >
          <!-- 排序字段 -->
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm text-n-slate-12">
              {{ t('PILOT.KNOWLEDGE_BASES.SORT.SORT_BY_LABEL') }}
            </span>
            <SelectMenu
              :model-value="activeSort"
              :options="sortMenus"
              :label="activeSortLabel"
              @update:model-value="handleSortChange"
            />
          </div>

          <!-- 排序顺序 -->
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm text-n-slate-12">
              {{ t('PILOT.KNOWLEDGE_BASES.SORT.ORDER_LABEL') }}
            </span>
            <SelectMenu
              :model-value="activeOrdering"
              :options="orderingMenus"
              :label="activeOrderingLabel"
              @update:model-value="handleOrderChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
